import { formatCurrency } from '@/lib/utils';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Calculator, CheckCircle, AlertTriangle, History, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

const DENOMINATIONS = [
  { label: 'R200', value: 200 },
  { label: 'R100', value: 100 },
  { label: 'R50', value: 50 },
  { label: 'R20', value: 20 },
  { label: 'R10', value: 10 },
  { label: 'R5', value: 5 },
  { label: 'R2', value: 2 },
  { label: 'R1', value: 1 },
  { label: '50c', value: 0.50 },
  { label: '20c', value: 0.20 },
  { label: '10c', value: 0.10 },
  { label: '5c', value: 0.05 },
];

const ITEMS_PER_PAGE = 10;

const CashCountPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [systemCashData, setSystemCashData] = useState({ total_system_cash: 0, breakdown: [] });
  const [countHistory, setCountHistory] = useState([]);
  const [editingCount, setEditingCount] = useState(null);
  const [editCounts, setEditCounts] = useState({});
  const [editDate, setEditDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const isAdmin = user?.role === 'admin';

  const [counts, setCounts] = useState(
    DENOMINATIONS.reduce((acc, d) => ({ ...acc, [d.label]: '' }), {})
  );

  const [countDate, setCountDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch system cash when date changes
  useEffect(() => {
    fetchSystemCash(countDate);
  }, [countDate]);

  const fetchData = async () => {
    try {
      const [systemCashRes, countsRes] = await Promise.all([
        api.get('/total-system-cash', { params: { as_of_date: countDate } }),
        api.get('/cash-counts')
      ]);
      setSystemCashData(systemCashRes.data);
      setCountHistory(countsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemCash = async (date) => {
    try {
      const res = await api.get('/total-system-cash', { params: { as_of_date: date } });
      setSystemCashData(res.data);
    } catch (error) {
      console.error('Failed to fetch system cash for date:', date);
    }
  };

  const handleCountChange = (denom, value) => {
    setCounts(prev => ({ ...prev, [denom]: value }));
  };

  const handleEditCountChange = (denom, value) => {
    setEditCounts(prev => ({ ...prev, [denom]: value }));
  };

  const calculateTotal = (denomCounts) => {
    return DENOMINATIONS.reduce((total, d) => {
      const count = parseInt(denomCounts[d.label]) || 0;
      return total + (count * d.value);
    }, 0);
  };

  const totalCounted = calculateTotal(counts);
  const systemCash = systemCashData.total_system_cash;
  const overUnder = totalCounted - systemCash;

  const handleSave = async () => {
    setSaving(true);
    try {
      const denominations = {};
      DENOMINATIONS.forEach(d => {
        denominations[d.label] = parseInt(counts[d.label]) || 0;
      });

      await api.post('/cash-counts', {
        denominations,
        count_date: countDate
      });

      toast.success('Cash count saved');
      clearCounts();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async (countId) => {
    setConfirming(true);
    try {
      await api.post(`/cash-counts/${countId}/confirm`);
      toast.success('Cash count confirmed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm');
    } finally {
      setConfirming(false);
    }
  };

  const handleEdit = (count) => {
    setEditingCount(count);
    setEditCounts(count.denominations || {});
    setEditDate(count.count_date);
  };

  const handleSaveEdit = async () => {
    if (!editingCount) return;
    
    try {
      const denominations = {};
      DENOMINATIONS.forEach(d => {
        denominations[d.label] = parseInt(editCounts[d.label]) || 0;
      });

      await api.put(`/cash-counts/${editingCount.id}`, {
        denominations,
        count_date: editDate
      });

      toast.success('Cash count updated');
      setEditingCount(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (countId) => {
    try {
      await api.delete(`/cash-counts/${countId}`);
      toast.success('Cash count deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };


  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const clearCounts = () => {
    setCounts(DENOMINATIONS.reduce((acc, d) => ({ ...acc, [d.label]: '' }), {}));
  };

  if (loading) {
    return (
      <div className="page-container">
        <Skeleton className="h-10 w-64 mb-6" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="page-container animate-slide-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A18]">Cash Count</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Count ALL cash across all businesses and branches
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Denomination Entry */}
        <Card data-testid="cash-count-form">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              New Cash Count
            </CardTitle>
            <CardDescription>
              Enter the count for each denomination
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Count Date</Label>
              <Input
                type="date"
                value={countDate}
                onChange={(e) => setCountDate(e.target.value)}
                className="w-48"
                data-testid="count-date-input"
              />
            </div>

            <Separator />

            <div className="grid gap-2">
              {DENOMINATIONS.map((denom) => {
                const count = parseInt(counts[denom.label]) || 0;
                const subtotal = count * denom.value;
                
                return (
                  <div key={denom.label} className="flex items-center gap-3">
                    <Label className="w-12 font-medium text-sm">{denom.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={counts[denom.label]}
                      onChange={(e) => handleCountChange(denom.label, e.target.value)}
                      placeholder="0"
                      className="w-20 font-mono text-center h-10"
                      data-testid={`count-${denom.label}`}
                    />
                    <span className="text-muted-foreground text-sm">×</span>
                    <span className="w-16 text-right font-mono text-sm">{formatCurrency(denom.value)}</span>
                    <span className="text-muted-foreground text-sm">=</span>
                    <span className="w-24 text-right font-mono font-medium text-sm">
                      {formatCurrency(subtotal)}
                    </span>
                  </div>
                );
              })}
            </div>

            <Separator />

            <div className="flex justify-between">
              <Button variant="outline" onClick={clearCounts} data-testid="clear-counts-btn">
                Clear All
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#006241] hover:bg-[#004d33]"
                data-testid="save-count-btn"
              >
                {saving ? 'Saving...' : 'Save Count'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="space-y-6">
          <Card data-testid="count-summary">
            <CardHeader>
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#F2F2F0] p-4 rounded-lg">
                <Label className="text-muted-foreground text-xs">Total Counted</Label>
                <p className="font-mono text-3xl font-bold text-[#006241]">
                  {formatCurrency(totalCounted)}
                </p>
              </div>

              <div className="p-4 border rounded-lg">
                <Label className="text-muted-foreground text-xs">
                  Current Cash Ledger Balance (All Reconciled)
                </Label>
                <p className="font-mono text-2xl font-bold text-[#1A1A18]">
                  {formatCurrency(systemCash)}
                </p>
                {systemCashData.breakdown && systemCashData.breakdown.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {systemCashData.breakdown.map((b) => (
                      <div key={b.business_id} className="flex justify-between text-sm text-muted-foreground">
                        <span>{b.business_name}</span>
                        <span className="font-mono">{formatCurrency(b.balance)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={`p-4 rounded-lg ${
                overUnder > 0 ? 'bg-green-50 border border-green-200' :
                overUnder < 0 ? 'bg-red-50 border border-red-200' :
                'bg-gray-50 border border-gray-200'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  {overUnder > 0 ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : overUnder < 0 ? (
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  ) : null}
                  <Label className="text-muted-foreground text-xs">
                    {overUnder > 0 ? 'Cash Over' : overUnder < 0 ? 'Cash Short' : 'Balanced'}
                  </Label>
                </div>
                <p className={`font-mono text-2xl font-bold ${
                  overUnder > 0 ? 'text-green-700' :
                  overUnder < 0 ? 'text-red-700' :
                  'text-gray-700'
                }`}>
                  {overUnder >= 0 ? '+' : ''}{formatCurrency(overUnder)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Cash Count History */}
      <Card className="mt-6" data-testid="cash-count-history">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Cash Count History
          </CardTitle>
          {countHistory.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {countHistory.length} total records
            </span>
          )}
        </CardHeader>
        <CardContent>
          {countHistory.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No cash counts recorded yet</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F2F2F0]">
                      <TableHead>Date</TableHead>
                      <TableHead>Counted By</TableHead>
                      <TableHead className="text-right">Total Counted</TableHead>
                      <TableHead className="text-right">System Cash</TableHead>
                      <TableHead className="text-right">Over/Under</TableHead>
                      <TableHead>Status</TableHead>
                      {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {countHistory
                      .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
                      .map((count) => (
                      <TableRow key={count.id} data-testid={`count-row-${count.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{count.count_date}</p>
                            <p className="text-xs text-muted-foreground">{formatDateTime(count.created_at)}</p>
                          </div>
                        </TableCell>
                        <TableCell>{count.created_by}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(count.total_counted)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(count.system_cash)}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-medium ${
                          count.over_under > 0 ? 'text-green-700' :
                          count.over_under < 0 ? 'text-red-700' : ''
                        }`}>
                          {count.over_under >= 0 ? '+' : ''}{formatCurrency(count.over_under)}
                        </TableCell>
                        <TableCell>
                          {count.over_under === 0 ? (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                              Balanced
                            </Badge>
                          ) : count.over_under < 0 ? (
                            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
                              Short
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
                              Over
                            </Badge>
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(count)}
                                title="Edit"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" title="Delete">
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Cash Count?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This action cannot be undone. This will permanently delete the cash count record from {count.count_date}.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(count.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {countHistory.length > ITEMS_PER_PAGE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, countHistory.length)} of {countHistory.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm px-3">
                      Page {currentPage} of {Math.ceil(countHistory.length / ITEMS_PER_PAGE)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(Math.ceil(countHistory.length / ITEMS_PER_PAGE), p + 1))}
                      disabled={currentPage >= Math.ceil(countHistory.length / ITEMS_PER_PAGE)}
                      data-testid="next-page-btn"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingCount} onOpenChange={(open) => !open && setEditingCount(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Cash Count</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Count Date</Label>
              <Input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="w-48"
              />
            </div>

            <Separator />

            <div className="grid gap-2">
              {DENOMINATIONS.map((denom) => {
                const count = parseInt(editCounts[denom.label]) || 0;
                const subtotal = count * denom.value;
                
                return (
                  <div key={denom.label} className="flex items-center gap-3">
                    <Label className="w-12 font-medium text-sm">{denom.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editCounts[denom.label] || ''}
                      onChange={(e) => handleEditCountChange(denom.label, e.target.value)}
                      placeholder="0"
                      className="w-20 font-mono text-center h-10"
                    />
                    <span className="text-muted-foreground text-sm">×</span>
                    <span className="w-16 text-right font-mono text-sm">{formatCurrency(denom.value)}</span>
                    <span className="text-muted-foreground text-sm">=</span>
                    <span className="w-24 text-right font-mono font-medium text-sm">
                      {formatCurrency(subtotal)}
                    </span>
                  </div>
                );
              })}
            </div>

            <Separator />

            <div className="bg-[#F2F2F0] p-4 rounded-lg">
              <Label className="text-muted-foreground text-xs">New Total</Label>
              <p className="font-mono text-2xl font-bold text-[#006241]">
                {formatCurrency(calculateTotal(editCounts))}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCount(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="bg-[#006241] hover:bg-[#004d33]">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashCountPage;
